import React, { useEffect, useMemo, useState } from 'react';
import { X, ExternalLink, ArrowRightLeft } from 'lucide-react';
import ContactDetailView from '../pages/crm/ContactDetailView';
import PaymentTermsField from './PaymentTermsField';
import { useNavStep } from '../lib/NavHistory';
import { OPPORTUNITY_STAGES, OPPORTUNITY_STAGE_LABELS, isGhlManagedRow } from '../lib/opportunityStages';

/**
 * ContactDrawer — slide-over VARIANT of the contact workspace (Lead Drill-Down
 * PR-A). It does NOT rebuild the workspace: it renders the existing
 * ContactDetailView, which loads via crm:contacts:full-detail — the SAME path
 * the full-page workspace uses, so every drawer open writes the same
 * phi_access_log + VIEW_CONTACT_FULL_DETAIL audit, and inherits the existing
 * quick actions (call / SMS / email / add-note / reassign owner #120 /
 * create-task / move-stage) and the unified notes timeline.
 *
 * The drawer adds only chrome: a backdrop, the slide-over panel, a close
 * control, and a "Full profile" link (host-provided) to open the full page
 * without losing the Pending-list context behind the drawer.
 *
 * RBAC unchanged, view-as-self: ContactDetailView derives every control from the
 * passed `user` permissions exactly as on the full page.
 *
 * ⚠ THAT CLAIM USED TO BE FALSE, AND THIS IS THE FIX.
 *
 * `canUpdate` was a PROP defaulting to `true`, and NEITHER call site
 * (src/pages/crm/MyDayTab.js, src/pages/CalendarOversightPage.js) passed it. So
 * the drawer granted write affordances to every viewer, while the full page
 * correctly derived the same flag from `crm:update` (CRMPage.js:823). A role
 * holding `crm:read` but not `crm:update` — `executive`, which is the CEO's role
 * since migration 157 — saw Edit and Add-Note controls in the drawer that the
 * full page hid, every one of which returns ACCESS_DENIED from the server. The
 * server was never wrong; the drawer was.
 *
 * It is now DERIVED from `user`, not accepted as a prop. Two reasons for that
 * shape rather than "pass it from both call sites":
 *   1. A prop with a permissive default is a bug waiting for a third call site.
 *      Deriving it means a new caller cannot get this wrong by omission.
 *   2. It makes the paragraph above TRUE, rather than a comment describing an
 *      intention the code did not implement.
 * Same idiom as its neighbours (MyDayTab.js:132, CalendarOversightPage.js:29).
 */
export default function ContactDrawer({
  contactId, user, allUsers, onClose, onFullProfile,
  /*
   * ⚠ Card P95 item 4. The opportunity the drawer was opened FROM, if any.
   * A contact can hold several, so the drawer cannot pick one on its own —
   * the board that was clicked is the only thing that knows which card this
   * is. Absent (My Day, Calendar Oversight) the quick action is not rendered
   * at all, rather than guessing.
   */
  opportunity = null,
  pipelines = null,
  onStageMoved = null,
}) {
  const [users, setUsers] = useState(allUsers || null);
  const canUpdate = (user?.permissions || []).includes('crm:update');

  // #65 Phase C1: the drawer is "open" whenever contactId is set — register it
  // as a single Back step so the global Back closes it before navigating.
  // Unconditional hook (above the `if (!contactId) return null` below). Label
  // is a GENERIC string, never the contact's name (no PHI in nav history).
  useNavStep({ active: !!contactId, label: 'Contact preview', onBack: () => onClose && onClose() });

  // Reassign-owner needs the assignable-user list. If the host didn't pass one,
  // fetch it (guarded — a viewer without users:read simply gets an empty picker;
  // the rest of the drawer is unaffected).
  useEffect(() => {
    if (allUsers || !window.zignagenixAPI || typeof window.zignagenixAPI.getUsers !== 'function') return;
    let alive = true;
    window.zignagenixAPI.getUsers()
      .then((r) => { if (alive) setUsers(Array.isArray(r) ? r : (r?.data || [])); })
      .catch(() => { if (alive) setUsers([]); }); // catch-ok: reassign picker degrades if users:read denied
    return () => { alive = false; };
  }, [allUsers]);

  // Esc closes the drawer.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!contactId) return null;

  return (
    <div style={backdrop} onClick={onClose} role="presentation">
      <div style={panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Contact detail">
        <div style={chrome}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onFullProfile && (
              <button style={linkBtn} onClick={() => onFullProfile(contactId)} aria-label="Open full profile">
                <ExternalLink size={13} /> Full profile
              </button>
            )}
            <MoveStageAction
              opportunity={opportunity}
              pipelines={pipelines}
              canUpdate={canUpdate}
              onMoved={onStageMoved}
            />
          </div>
          <button style={closeBtn} onClick={onClose} aria-label="Close contact drawer"><X size={16} /></button>
        </div>
        <div style={body} className="responsive-scroll">
          <ContactDetailView
            contactId={contactId}
            onBack={onClose}
            allUsers={users || []}
            canUpdate={canUpdate}
            user={user}
          />
          {/*
            * ⭐ Card P161 item 3 — a client's payment terms, where the client
            * is. The component gates itself: no finance:read renders nothing,
            * and the editor appears only with finance:terms:write, so no
            * permission logic is duplicated here.
            */}
          <PaymentTermsField contactId={contactId} user={user} />
        </div>
      </div>
    </div>
  );
}

/**
 * ⛔⛔ MOVE STAGE — the quick action the parity audit found missing.
 *
 * ⚠⚠ IT CALLS THE SAME HANDLER THE BOARD'S DRAG CALLS, and that is the whole
 * design rather than a convenience: `crm:opportunities:move-stage` runs under
 * permission `crm:update`, and inside its transaction it sets the
 * `zg.user_id` / `zg.source` GUCs that migration 154's trigger reads to write
 * crm_stage_history. A second code path that UPDATEd the stage directly would
 * move the card and silently write NO HISTORY — the stage would change and
 * nothing would record who moved it or when.
 *
 * ⚠ THE STAGES COME FROM THE OPPORTUNITY'S OWN PIPELINE, not from
 * OpportunitiesTab's DEFAULT_STAGES, which is a hardcoded seven-item list that
 * belongs to no pipeline. Offering a rep a stage their pipeline does not have
 * is offering them a move that means nothing.
 *
 * ⚠ Rendered only when there IS an opportunity and the user holds crm:update.
 * An affordance that refuses is a promise the screen cannot keep.
 */
function MoveStageAction({ opportunity, pipelines, canUpdate, onMoved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  /*
   * ⛔⛔ THE STAGES THE OPPORTUNITIES BOARD USES — Card P95b item 2.
   *
   * This read `pipelines[].stages`, which are crm_pipeline_stages ROWS whose
   * `id` is a NUMERIC row id. move-stage writes that value straight into
   * crm_opportunities.stage — a free-text column every other reader treats as
   * a SLUG. So a drawer move set the stage to e.g. '42', the card vanished
   * from every column of the board, from the stage filter and from the edit
   * form, and crm_stage_history recorded '42' permanently. '(current)' never
   * matched either, because a slug is never equal to a row id.
   *
   * ⚠⚠ MY TEST PASSED BECAUSE ITS FIXTURE USED SLUG IDS. A fixture that does
   * not have the shape of the real data proves the code works on the fixture.
   * The arms now use rows shaped like crm_pipeline_stages — numeric ids.
   *
   * ⚠ crm_pipeline_stages is a DIFFERENT stage model, not a better source: the
   * Pipeline board groups by ghl_stage_id against those rows, the Opportunities
   * board by this free-text column. Reconciling them is its own card; until
   * then anything writing this column uses src/lib/opportunityStages.js.
   */
  const stages = useMemo(
    () => OPPORTUNITY_STAGES.map((slug) => ({ id: slug, name: OPPORTUNITY_STAGE_LABELS[slug] || slug })),
    []);

  if (!opportunity || !canUpdate) return null;
  if (stages.length === 0) return null;

  /*
   * ⛔⛔ AN IMPORTED ROW IS SHOWN, NEVER OFFERED — Card P95c item 1.
   *
   * GHL-imported opportunities carry Carlos's canonical names from
   * crm_pipeline_stages (migrations 136/137), e.g. 'Doctor Consult Booked'.
   * The forecast and the workflow role definitions key on those long names, so
   * writing a slug over one drops the opportunity out of its forecast tier and
   * out of the PEM's owned_stages — silently, and with a crm_stage_history row
   * that no report can interpret.
   *
   * ⚠ THE STAGE IS STILL DISPLAYED. Hiding it would leave the rep unable to
   * see what stage they are looking at; what they cannot do is change it here.
   * The server refuses this too — this is the half that stops the rep being
   * offered a button that will fail.
   */
  /*
   * ⛔⛔ isGhlManagedRow, NOT the stage alone — Card P95g item 2.
   *
   * ghlOpportunityUpsert.keywordStage gives an unmapped GHL deal a SLUG stage
   * ('proposal', 'qualified', 'contacted'), so a stage-only check showed the
   * Move stage control on a deal the server now refuses — and, before that
   * refusal existed, let the rep cut it off from GHL.
   */
  if (isGhlManagedRow(opportunity)) {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} data-ghl-managed-stage>
        <span style={readOnlyStage}>{opportunity.stage}</span>
        <span style={managedNote}>Stage managed by GHL pipeline</span>
      </div>
    );
  }

  // ⚠ The SLUG, never a row id — this is what the board groups by.
  const currentId = opportunity.stage;

  const move = async (stage) => {
    const target = stage.id ?? stage.stage_id ?? stage.key ?? stage.name;
    if (String(target) === String(currentId)) { setOpen(false); return; }
    setBusy(true); setErr(null);
    try {
      const api = window.zignagenixAPI;
      if (!api || typeof api.moveCRMOpportunityStage !== 'function') {
        throw new Error('Moving a stage is not available in this window.');
      }
      const r = await api.moveCRMOpportunityStage(opportunity.id, target);
      if (r && r.success === false) throw new Error(r.error || 'The move was refused.');
      setOpen(false);
      /*
       * ⛔ Item 5: the card BEHIND the drawer refreshes, with no full reload.
       * The host is handed the new stage so it can update that one card.
       */
      if (onMoved) onMoved({ opportunityId: opportunity.id, stage: target });
    } catch (e) {
      setErr(e && e.message ? e.message : 'The move failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        style={linkBtn}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        aria-label="Move stage"
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
      >
        <ArrowRightLeft size={13} /> {busy ? 'Moving…' : 'Move stage'}
      </button>
      {open && (
        <div role="menu" aria-label="Move to stage" style={stageMenu}>
          {stages.map((st) => {
            const id = st.id ?? st.stage_id ?? st.key ?? st.name;
            const isCurrent = String(id) === String(currentId);
            return (
              <button
                key={String(id)}
                role="menuitem"
                disabled={busy || isCurrent}
                onClick={() => move(st)}
                style={{ ...stageItem, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? '#6C757D' : '#111827' }}
              >
                {st.name || st.label || String(id)}{isCurrent ? ' (current)' : ''}
              </button>
            );
          })}
        </div>
      )}
      {err && <div role="alert" style={stageErr}>{err}</div>}
    </div>
  );
}

const readOnlyStage = { display: 'inline-flex', alignItems: 'center', padding: '5px 10px', fontSize: 12, fontWeight: 600, background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0', borderRadius: 6 };
const managedNote = { fontSize: 11, color: '#6C757D' };
const stageMenu = { position: 'absolute', top: '100%', left: 0, marginTop: 4, background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 1100, minWidth: 180, padding: 4 };
const stageItem = { display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: 12, background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer' };
const stageErr = { position: 'absolute', top: '100%', left: 0, marginTop: 4, fontSize: 11, color: '#DC3545', background: '#fff', padding: '4px 8px', borderRadius: 6, whiteSpace: 'nowrap', zIndex: 1100 };

const backdrop = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' };
const panel = { width: 'min(680px, 96vw)', height: '100%', background: '#fff', boxShadow: '-8px 0 28px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', animation: 'none' };
const chrome = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', borderBottom: '1px solid #e9ecef', background: '#f8fafc' };
const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', fontSize: 12, fontWeight: 600, background: '#fff', color: '#2E8B98', border: '1px solid #cfe3e6', borderRadius: 6, cursor: 'pointer' };
const closeBtn = { display: 'inline-flex', alignItems: 'center', padding: '5px 8px', background: '#fff', color: '#5a6478', border: '1px solid #dee2e6', borderRadius: 6, cursor: 'pointer' };
const body = { flex: 1, overflowY: 'auto', padding: '4px 12px 16px' };
